// Ajout ET modification d'un site surveillé.
//
// L'essentiel (adresse, méthode, intervalle, qui prévenir) est en haut ; la
// requête détaillée (en-têtes, corps, authentification) et la réponse attendue
// sont repliées tant qu'on n'y a rien mis. « Tester » sonde le formulaire tel
// quel depuis le serveur RMM — le même chemin réseau que la surveillance.
//
// `monitor` nul = ajout ; sinon la fiche détaillée (mot de passe HTTP vide = inchangé).

import { useEffect, useMemo, useState } from 'react';
import { X, Globe, CheckCircle2, AlertTriangle, Loader2, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { ClientPicker } from './ClientPicker.jsx';
import { useDashboard } from '../views/dashboardContext.jsx';
import { wmCall, fmtDateTime } from '../lib/webmonitor.jsx';

const labelCls = 'block text-[11px] text-muted-2 mb-1';
const inputCls =
  'w-full bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none focus:border-blue-500';
const METHODS = ['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'];
const BODY_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
const INTERVALS = [60, 120, 300, 600, 900, 1800, 3600];

function Section({ title, open, onToggle, hint, children }) {
  return (
    <div className="border border-rule rounded-xl mt-3">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-1.5 px-3.5 py-2.5 bg-transparent border-none cursor-pointer text-left text-[12px] font-semibold text-fg-2"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {title}
        {!open && hint && <span className="font-normal text-muted-2 ml-1 truncate">{hint}</span>}
      </button>
      {open && <div className="px-3.5 pb-3.5">{children}</div>}
    </div>
  );
}

export function WebMonitorModal({ monitor, onClose }) {
  const { user } = useDashboard();
  const editing = !!monitor;
  const [form, setForm] = useState(() => ({
    name: monitor?.name || '',
    url: monitor?.url || '',
    method: monitor?.method || 'GET',
    intervalSeconds: monitor?.intervalSeconds || 300,
    timeoutSeconds: monitor?.timeoutSeconds || 30,
    confirmationCount: monitor?.confirmationCount || 2,
    expectedStatusCodes: monitor?.expectedStatusCodes || '200-399',
    keyword: monitor?.keyword || '',
    keywordMustBeAbsent: !!monitor?.keywordMustBeAbsent,
    keywordCaseSensitive: !!monitor?.keywordCaseSensitive,
    headers: monitor?.headers?.length ? monitor.headers.map((h) => ({ ...h })) : [],
    body: monitor?.body || '',
    bodyContentType: monitor?.bodyContentType || 'application/json',
    basicAuthUser: monitor?.basicAuthUser || '',
    basicAuthPassword: '',
    followRedirects: monitor ? !!monitor.followRedirects : true,
    verifyTls: monitor ? !!monitor.verifyTls : true,
    certWarningDays: monitor ? monitor.certWarningDays : 14,
    enabled: monitor ? !!monitor.enabled : true,
    clientId: monitor?.clientId || null,
    agentId: monitor?.agentId || null,
    notifyUserIds: monitor?.notifyUserIds || [],
    notifyEmails: monitor?.notifyEmails || '',
  }));
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [openRequest, setOpenRequest] = useState(
    () => !!(monitor && (monitor.headers?.length || monitor.body || monitor.basicAuthUser))
  );
  const [openResponse, setOpenResponse] = useState(
    () => !!(monitor && (monitor.keyword || monitor.expectedStatusCodes !== '200-399' || !monitor.followRedirects || !monitor.verifyTls))
  );
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/platform/web-monitors/recipients').then((list) => {
      if (!alive || !list) return;
      setUsers(list);
      // À l'ajout, celui qui crée le site est prévenu par défaut.
      if (!editing && user?.email) {
        const me = list.find((u) => u.email.toLowerCase() === user.email.toLowerCase());
        if (me) setForm((f) => (f.notifyUserIds.length ? f : { ...f, notifyUserIds: [me.id] }));
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Serveurs du client choisi, serveurs d'abord.
  useEffect(() => {
    if (!form.clientId) {
      setDevices([]);
      return;
    }
    let alive = true;
    api.get('/devices?clientId=' + encodeURIComponent(form.clientId)).then((list) => {
      if (!alive) return;
      const rows = (list || []).filter((d) => d.platform !== 'routeros');
      rows.sort(
        (a, b) =>
          (a.machineType === 'Server' ? 0 : 1) - (b.machineType === 'Server' ? 0 : 1) ||
          (a.label || a.computerName).localeCompare(b.label || b.computerName)
      );
      setDevices(rows);
    });
    return () => {
      alive = false;
    };
  }, [form.clientId]);

  const set = (patch, resetsTest = true) => {
    setForm((f) => ({ ...f, ...patch }));
    if (resetsTest) setTest(null);
    setError(null);
  };
  const setHeader = (i, patch) => set({ headers: form.headers.map((h, j) => (j === i ? { ...h, ...patch } : h)) });

  const payload = () => ({
    ...form,
    headers: form.headers.filter((h) => h.name.trim() || h.value),
    basicAuthPassword: form.basicAuthPassword || (editing && monitor.hasBasicAuthPassword ? null : ''),
    monitorId: monitor?.id || null,
  });

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setError(null);
    const r = await wmCall('/test', 'POST', payload());
    setTesting(false);
    if (r.ok) setTest(r.json);
    else setError(r.json?.error || 'Test impossible (erreur ' + r.status + ').');
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    const r = editing ? await wmCall('/' + monitor.id, 'PUT', payload()) : await wmCall('', 'POST', payload());
    setSaving(false);
    if (r.ok) onClose(r.json?.id || true);
    else setError(r.json?.error || "Échec de l'enregistrement (erreur " + r.status + ').');
  };

  const available = useMemo(() => users.filter((u) => !form.notifyUserIds.includes(u.id)), [users, form.notifyUserIds]);
  const chosen = form.notifyUserIds.map((id) => users.find((u) => u.id === id) || { id, name: id, email: '' });
  const nobody = form.notifyUserIds.length === 0 && !form.notifyEmails.trim();
  const canSubmit = form.url.trim() && !saving;
  const certDays = test?.certNotAfter ? Math.floor((new Date(test.certNotAfter).getTime() - Date.now()) / 86400000) : null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[640px] max-w-full max-h-[92vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-3 flex-none flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe size={18} strokeWidth={1.75} className="text-blue-400" />
            <h2 className="text-base font-bold text-fg">{editing ? 'Modifier le site surveillé' : 'Surveiller un site web'}</h2>
          </div>
          <button
            onClick={() => onClose(false)}
            className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
            aria-label="Fermer"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          {/* ─── Adresse ─── */}
          <label className={labelCls}>Adresse</label>
          <div className="flex gap-2">
            <select
              value={form.method}
              onChange={(e) => set({ method: e.target.value, ...(e.target.value === 'HEAD' ? { keyword: '' } : {}) })}
              className={`${inputCls} !w-[100px] font-mono font-semibold`}
            >
              {METHODS.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
            <input
              className={`${inputCls} font-mono`}
              placeholder="https://exemple.com/connexion"
              value={form.url}
              autoFocus={!editing}
              onChange={(e) => set({ url: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-3 mt-3">
            <div>
              <label className={labelCls}>Nom affiché (facultatif)</label>
              <input
                className={inputCls}
                placeholder="par défaut : l'adresse sans https://"
                value={form.name}
                onChange={(e) => set({ name: e.target.value }, false)}
              />
            </div>
            <div>
              <label className={labelCls}>Vérifier toutes les</label>
              <select
                className={inputCls}
                value={form.intervalSeconds}
                onChange={(e) => set({ intervalSeconds: Number(e.target.value) }, false)}
              >
                {INTERVALS.map((s) => (
                  <option key={s} value={s}>
                    {s < 3600 ? s / 60 + ' min' : '1 h'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ─── Qui prévenir ─── */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className={labelCls}>Techniciens prévenus par courriel</label>
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {chosen.map((u) => (
                  <span
                    key={u.id}
                    title={u.email}
                    className="inline-flex items-center gap-1 bg-blue-500/15 text-blue-400 rounded-md pl-2 pr-1 py-0.5 text-[11px] font-semibold"
                  >
                    {u.name}
                    <button
                      type="button"
                      onClick={() => set({ notifyUserIds: form.notifyUserIds.filter((x) => x !== u.id) }, false)}
                      className="bg-transparent border-none cursor-pointer text-blue-400 hover:text-fg p-0 leading-none"
                      aria-label={'Retirer ' + u.name}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <select
                className={inputCls}
                value=""
                onChange={(e) => e.target.value && set({ notifyUserIds: [...form.notifyUserIds, e.target.value] }, false)}
              >
                <option value="">{available.length ? 'Ajouter un technicien…' : 'Tous les techniciens sont choisis'}</option>
                {available.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} — {u.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Autres adresses (facultatif)</label>
              <input
                className={inputCls}
                placeholder="client@exemple.com, …"
                value={form.notifyEmails}
                onChange={(e) => set({ notifyEmails: e.target.value }, false)}
              />
              {nobody && (
                <div className="text-[11px] text-warn mt-1.5 flex items-center gap-1">
                  <AlertTriangle size={12} /> Personne ne sera prévenu d'une panne.
                </div>
              )}
            </div>
          </div>

          {/* ─── Rattachement ─── */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className={labelCls}>Client (facultatif)</label>
              <ClientPicker
                value={form.clientId}
                onChange={(id) => set({ clientId: id || null, agentId: null }, false)}
                placeholder="Aucun (interne)"
              />
            </div>
            <div>
              <label className={labelCls}>Serveur qui l'héberge (facultatif)</label>
              <select
                className={inputCls}
                value={form.agentId || ''}
                disabled={!form.clientId}
                onChange={(e) => set({ agentId: e.target.value ? Number(e.target.value) : null }, false)}
              >
                <option value="">{form.clientId ? 'Aucun' : "Choisir d'abord le client"}</option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label || d.computerName}
                    {d.machineType === 'Server' ? '' : ' (poste)'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {form.agentId && (
            <div className="text-[11px] text-muted-2 mt-1.5">
              Une panne devient aussi une alerte de ce serveur (page Alertes, bon ComIT) et se tait pendant ses
              fenêtres de maintenance.
            </div>
          )}

          {/* ─── Requête ─── */}
          <Section
            title="Requête"
            open={openRequest}
            onToggle={() => setOpenRequest((o) => !o)}
            hint="en-têtes, corps, authentification"
          >
            <label className={labelCls}>En-têtes</label>
            {form.headers.map((h, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <input
                  className={`${inputCls} font-mono !w-[40%]`}
                  placeholder="Nom"
                  value={h.name}
                  onChange={(e) => setHeader(i, { name: e.target.value })}
                />
                <input
                  className={`${inputCls} font-mono`}
                  placeholder="Valeur"
                  value={h.value}
                  onChange={(e) => setHeader(i, { value: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => set({ headers: form.headers.filter((_, j) => j !== i) })}
                  className="bg-transparent border-none text-muted cursor-pointer hover:text-crit px-1"
                  aria-label="Retirer l'en-tête"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set({ headers: [...form.headers, { name: '', value: '' }] })}
              className="bg-transparent border-none text-blue-400 cursor-pointer text-[11px] font-semibold p-0 inline-flex items-center gap-1"
            >
              <Plus size={12} /> Ajouter un en-tête
            </button>
            {monitor && monitor.headersReadable === false && (
              <div className="text-[11px] text-crit mt-1.5">
                Les en-têtes enregistrés sont illisibles (clés de chiffrement absentes) : ressaisissez-les.
              </div>
            )}

            {BODY_METHODS.includes(form.method) && (
              <div className="mt-3">
                <div className="flex items-end justify-between gap-2 mb-1">
                  <label className={`${labelCls} !mb-0`}>Corps</label>
                  <input
                    className={`${inputCls} font-mono !w-[200px] !py-1`}
                    value={form.bodyContentType}
                    onChange={(e) => set({ bodyContentType: e.target.value })}
                    title="Type de contenu"
                  />
                </div>
                <textarea
                  className={`${inputCls} font-mono h-20 resize-y`}
                  value={form.body}
                  onChange={(e) => set({ body: e.target.value })}
                  placeholder='{"ping": true}'
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className={labelCls}>Authentification HTTP — utilisateur</label>
                <input
                  className={inputCls}
                  value={form.basicAuthUser}
                  autoComplete="off"
                  onChange={(e) => set({ basicAuthUser: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Mot de passe</label>
                <input
                  type="password"
                  className={inputCls}
                  autoComplete="new-password"
                  disabled={!form.basicAuthUser}
                  placeholder={editing && monitor.hasBasicAuthPassword ? '(inchangé)' : ''}
                  value={form.basicAuthPassword}
                  onChange={(e) => set({ basicAuthPassword: e.target.value })}
                />
              </div>
            </div>
          </Section>

          {/* ─── Réponse attendue ─── */}
          <Section
            title="Réponse attendue"
            open={openResponse}
            onToggle={() => setOpenResponse((o) => !o)}
            hint={`codes ${form.expectedStatusCodes}${form.keyword ? ', mot-clé' : ''}`}
          >
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Codes HTTP acceptés</label>
                <input
                  className={`${inputCls} font-mono`}
                  value={form.expectedStatusCodes}
                  onChange={(e) => set({ expectedStatusCodes: e.target.value })}
                  title="Ex. : 200-399, 2xx, 200,301"
                />
              </div>
              <div>
                <label className={labelCls}>Délai d'attente (s)</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  className={inputCls}
                  value={form.timeoutSeconds}
                  onChange={(e) => set({ timeoutSeconds: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className={labelCls}>Panne déclarée après</label>
                <select
                  className={inputCls}
                  value={form.confirmationCount}
                  onChange={(e) => set({ confirmationCount: Number(e.target.value) }, false)}
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n} échec{n > 1 ? 's' : ''} de suite
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-3 mt-3">
              <div>
                <label className={labelCls}>Mot-clé dans la page (facultatif)</label>
                <input
                  className={inputCls}
                  disabled={form.method === 'HEAD'}
                  placeholder={form.method === 'HEAD' ? 'pas de page avec HEAD' : 'ex. : Connexion'}
                  value={form.keyword}
                  onChange={(e) => set({ keyword: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>&nbsp;</label>
                <select
                  className={inputCls}
                  disabled={!form.keyword}
                  value={form.keywordMustBeAbsent ? 'absent' : 'present'}
                  onChange={(e) => set({ keywordMustBeAbsent: e.target.value === 'absent' })}
                >
                  <option value="present">doit être présent</option>
                  <option value="absent">ne doit pas être présent</option>
                </select>
              </div>
            </div>
            {form.keyword && (
              <label className="flex items-center gap-2 mt-2 text-[12px] text-fg-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.keywordCaseSensitive}
                  onChange={(e) => set({ keywordCaseSensitive: e.target.checked })}
                />
                Respecter la casse
              </label>
            )}

            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-[12px] text-fg-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.followRedirects}
                  onChange={(e) => set({ followRedirects: e.target.checked })}
                />
                Suivre les redirections
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.verifyTls} onChange={(e) => set({ verifyTls: e.target.checked })} />
                Exiger un certificat valide
              </label>
              <label className="flex items-center gap-2">
                Avertir
                <input
                  type="number"
                  min={0}
                  max={90}
                  className={`${inputCls} !w-[60px] !py-1`}
                  value={form.certWarningDays}
                  onChange={(e) => set({ certWarningDays: Number(e.target.value) }, false)}
                />
                jours avant l'expiration du certificat (0 = jamais)
              </label>
            </div>
          </Section>

          {/* ─── Test ─── */}
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={runTest}
              disabled={testing || !form.url.trim()}
              className="bg-rule border border-rule-3 rounded-lg px-3.5 py-2 text-fg-2 cursor-pointer text-xs font-semibold hover:bg-rule-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {testing && <Loader2 size={13} className="animate-spin" />}
              {testing ? 'Test en cours…' : 'Tester maintenant'}
            </button>
            {!test && <span className="text-[11px] text-muted-2">Envoie la requête depuis le serveur RMM, sans enregistrer.</span>}
          </div>

          {test && (
            <div
              className={`mt-3 rounded-xl px-3.5 py-3 text-[12px] text-fg-2 border ${
                test.up ? 'bg-ok/10 border-ok/30' : 'bg-crit/10 border-crit/30'
              }`}
            >
              <div className={`flex items-center gap-1.5 font-semibold mb-1.5 ${test.up ? 'text-ok' : 'text-crit'}`}>
                {test.up ? <CheckCircle2 size={14} strokeWidth={2} /> : <AlertTriangle size={14} strokeWidth={2} />}
                {test.up ? 'Le site répond comme attendu' : test.error}
              </div>
              <div className="grid grid-cols-[110px_1fr] gap-y-0.5">
                <span className="text-muted-2">Réponse</span>
                <span className="font-mono">
                  {test.statusCode ? `HTTP ${test.statusCode} ${test.statusText || ''}` : '—'}
                  {test.responseMs != null && ` en ${test.responseMs} ms`}
                </span>
                {test.finalUrl && test.finalUrl !== form.url.trim() && (
                  <>
                    <span className="text-muted-2">Après redirection</span>
                    <span className="font-mono break-all">{test.finalUrl}</span>
                  </>
                )}
                {test.certNotAfter && (
                  <>
                    <span className="text-muted-2">Certificat</span>
                    <span>
                      valide jusqu'au {fmtDateTime(test.certNotAfter)}{' '}
                      <span className={certDays < 14 ? 'text-warn' : 'text-muted-2'}>({certDays} j)</span>
                      {test.certIssuer && <span className="text-muted-2"> · {test.certIssuer}</span>}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-3 bg-crit/10 border border-crit/30 rounded-xl px-3.5 py-2.5 text-[12px] text-fg-2 flex gap-2">
              <AlertTriangle size={14} strokeWidth={1.75} className="text-crit flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2.5 mt-5">
            <button
              onClick={() => onClose(false)}
              className="bg-rule border border-rule-3 rounded-lg px-4 py-2 text-muted cursor-pointer text-[13px] font-semibold"
            >
              Annuler
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
            >
              {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
